import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  MdArrowBack,
  MdArrowForward,
  MdShield,
  MdCheckCircle,
  MdImage,
  MdVpnKey,
  MdScience,
  MdHelpOutline,
} from 'react-icons/md';
import { useTranslation } from 'i18n';
import { getPlatformBySlug, PLATFORMS } from './integrations/data';
import { localizePlatform } from './integrations/i18n';

// Rendered tại /integrations/:slug. Đọc config từ integrations/data.js, sau đó
// merge VI overrides qua localizePlatform(). Các section optional
// (useCases, scopeJustifications, testingSteps, faq) chỉ render nếu data tồn tại.
export default function IntegrationPage() {
  const { slug } = useParams();
  const { lang, t } = useTranslation();
  const base = getPlatformBySlug(slug);
  const platform = base ? localizePlatform(base, lang) : null;

  if (!platform) {
    return (
      <Container maxW="container.md" py={{ base: 16, md: 24 }} textAlign="center">
        <Heading size="lg" mb={3}>
          {t('integrations.notFound.title')}
        </Heading>
        <Text color="gray.500" mb={6}>
          {t('integrations.notFound.desc', { slug })}
        </Text>
        <Button as={RouterLink} to="/integrations" colorScheme="blue" leftIcon={<MdArrowBack />}>
          {t('integrations.notFound.cta')}
        </Button>
      </Container>
    );
  }

  const IconComp = platform.icon;
  const hasShowcase = (platform.screenshots?.length || 0) > 0;
  const hasUseCases = (platform.useCases?.length || 0) > 0;
  const hasScopeJust = (platform.scopeJustifications?.length || 0) > 0;
  const hasTestingSteps = (platform.testingSteps?.length || 0) > 0;
  const hasFaq = (platform.faq?.length || 0) > 0;
  const otherPlatforms = PLATFORMS.filter(p => p.slug !== platform.slug).slice(0, 4);

  return (
    <Box>
      {/* ===== Hero ===== */}
      <Box
        bg={`${platform.color}08`}
        borderBottom="1px solid"
        borderColor="gray.100"
        py={{ base: 12, md: 16 }}
      >
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Button
            as={RouterLink}
            to="/integrations"
            variant="ghost"
            size="sm"
            leftIcon={<MdArrowBack />}
            mb={{ base: 6, md: 8 }}
            color="gray.600"
          >
            {t('integrations.backAll')}
          </Button>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 6, md: 8 }}
            align={{ base: 'flex-start', md: 'center' }}
          >
            <Flex
              w={{ base: '72px', md: '96px' }}
              h={{ base: '72px', md: '96px' }}
              borderRadius="20px"
              bg={`${platform.color}15`}
              align="center"
              justify="center"
              color={platform.color}
              flexShrink={0}
            >
              <IconComp size={48} />
            </Flex>
            <Box flex={1} minW={0}>
              <Tag
                size="sm"
                colorScheme={hasShowcase ? 'green' : 'gray'}
                borderRadius="full"
                px={3}
                mb={3}
              >
                {hasShowcase ? t('integrations.hero.live') : t('integrations.hero.coming')}
              </Tag>
              <Heading
                fontSize={{ base: '2xl', md: '4xl' }}
                fontWeight={800}
                color="gray.900"
                mb={3}
              >
                {t('integrations.hero.titleSuffix', { name: platform.name })}
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="3xl">
                {platform.description}
              </Text>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* ===== Use Cases ===== */}
      {hasUseCases && (
        <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
            <Tag size="sm" colorScheme="purple" borderRadius="full" px={3}>
              <Text fontSize="xs" fontWeight={700}>
                {t('integrations.useCases.badge')}
              </Text>
            </Tag>
            <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
              {t('integrations.useCases.title', { name: platform.name })}
            </Heading>
            <Text color="gray.600" maxW="2xl">
              {t('integrations.useCases.subtitle')}
            </Text>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 5 }}>
            {platform.useCases.map(uc => {
              const UCIcon = uc.icon;
              return (
                <Flex
                  key={uc.title}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="14px"
                  p={{ base: 5, md: 6 }}
                  gap={4}
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                    borderColor: `${platform.color}30`,
                  }}
                >
                  <Flex
                    w="44px"
                    h="44px"
                    borderRadius="12px"
                    bg={`${platform.color}15`}
                    align="center"
                    justify="center"
                    color={platform.color}
                    flexShrink={0}
                  >
                    <UCIcon size={22} />
                  </Flex>
                  <Box>
                    <Heading fontSize="md" fontWeight={700} color="gray.900" mb={2}>
                      {uc.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                      {uc.desc}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </SimpleGrid>
        </Container>
      )}

      {/* ===== Products & Scopes summary ===== */}
      <Box bg="gray.50" py={{ base: 10, md: 14 }} borderY="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 5, md: 6 }}>
            <Box
              bg="white"
              borderRadius="14px"
              border="1px solid"
              borderColor="gray.100"
              p={{ base: 5, md: 6 }}
            >
              <Text
                fontSize="xs"
                fontWeight={700}
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={3}
              >
                {t('integrations.products')}
              </Text>
              <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                {platform.products.map(p => (
                  <Tag key={p} size="md" colorScheme="blue" borderRadius="md" fontWeight={600}>
                    {p}
                  </Tag>
                ))}
              </HStack>
            </Box>
            <Box
              bg="white"
              borderRadius="14px"
              border="1px solid"
              borderColor="gray.100"
              p={{ base: 5, md: 6 }}
            >
              <Text
                fontSize="xs"
                fontWeight={700}
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={3}
              >
                {t('integrations.scopes')}
              </Text>
              <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                {platform.scopes.map(s => (
                  <Tag
                    key={s}
                    size="sm"
                    bg="gray.100"
                    color="gray.700"
                    borderRadius="md"
                    fontFamily="mono"
                    fontWeight={600}
                  >
                    {s}
                  </Tag>
                ))}
              </HStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ===== Scope Justification (detailed per scope) ===== */}
      {hasScopeJust && (
        <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
            <Tag size="sm" colorScheme="blue" borderRadius="full" px={3}>
              <HStack spacing={1.5}>
                <Icon as={MdVpnKey} boxSize={3} />
                <Text fontSize="xs" fontWeight={700}>
                  {t('integrations.scopeJust.badge')}
                </Text>
              </HStack>
            </Tag>
            <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
              {t('integrations.scopeJust.title')}
            </Heading>
            <Text color="gray.600" maxW="2xl">
              {t('integrations.scopeJust.subtitle')}
            </Text>
          </Stack>
          <VStack
            spacing={0}
            align="stretch"
            maxW="4xl"
            mx="auto"
            bg="white"
            borderRadius="14px"
            border="1px solid"
            borderColor="gray.100"
            divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}
          >
            {platform.scopeJustifications.map(j => (
              <Box key={j.scope} p={{ base: 5, md: 6 }}>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={{ base: 3, md: 6 }}
                  align="flex-start"
                >
                  <Box minW={{ md: '220px' }} flexShrink={0}>
                    <Tag
                      bg="gray.900"
                      color="white"
                      borderRadius="md"
                      fontFamily="mono"
                      fontSize="xs"
                      fontWeight={700}
                      px={2.5}
                      py={1}
                    >
                      {j.scope}
                    </Tag>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      mt={2}
                      textTransform="uppercase"
                      letterSpacing="wider"
                      fontWeight={600}
                    >
                      {t('integrations.scopeJust.usedBy')}
                    </Text>
                    <Text fontSize="sm" fontWeight={700} color="gray.800" mt={0.5}>
                      {j.feature}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.7" flex={1}>
                    {j.reason}
                  </Text>
                </Flex>
              </Box>
            ))}
          </VStack>
        </Container>
      )}

      {/* ===== Showcase or Placeholder ===== */}
      <Box bg="gray.50" py={{ base: 12, md: 16 }} borderY="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
            <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
              {hasShowcase
                ? t('integrations.showcase.title', { name: platform.name })
                : t('integrations.showcase.coming')}
            </Heading>
            {hasShowcase ? (
              <Text color="gray.600" maxW="2xl">
                {t('integrations.showcase.subtitle')}
              </Text>
            ) : (
              <Text color="gray.600" maxW="2xl">
                {t('integrations.showcase.comingDesc', { name: platform.name })}
              </Text>
            )}
          </Stack>

          {hasShowcase ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 5, md: 6 }}>
              {platform.screenshots.map(shot => (
                <Box
                  key={shot.step}
                  bg="white"
                  borderRadius="16px"
                  border="1px solid"
                  borderColor="gray.100"
                  overflow="hidden"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-4px)',
                    boxShadow: 'xl',
                    borderColor: 'gray.200',
                  }}
                  display="flex"
                  flexDirection="column"
                >
                  <Box
                    bg="gray.50"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    position="relative"
                    overflow="hidden"
                  >
                    <Image
                      src={shot.src}
                      alt={shot.title}
                      w="full"
                      h={{ base: '200px', md: '220px' }}
                      objectFit="cover"
                      objectPosition="top"
                    />
                    <Tag
                      position="absolute"
                      top={3}
                      left={3}
                      size="sm"
                      bg="blackAlpha.800"
                      color="white"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontWeight={700}
                      fontSize="xs"
                    >
                      {shot.step}
                    </Tag>
                  </Box>
                  <Box p={{ base: 4, md: 5 }} flex={1}>
                    <Heading fontSize="md" fontWeight={700} color="gray.900" mb={2}>
                      {shot.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                      {shot.desc}
                    </Text>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              bg="white"
              borderRadius="16px"
              border="2px dashed"
              borderColor="gray.200"
              py={{ base: 12, md: 16 }}
              px={6}
              maxW="2xl"
              mx="auto"
              textAlign="center"
            >
              <Icon as={MdImage} boxSize={12} color="gray.300" mb={4} />
              <Text color="gray.500" fontSize="sm" maxW="md">
                {t('integrations.placeholder.text', { name: platform.name })}
              </Text>
              <Button
                as={RouterLink}
                to="/auth/sign-in"
                size="sm"
                colorScheme="blue"
                mt={5}
                rightIcon={<MdArrowForward />}
              >
                {t('integrations.placeholder.cta')}
              </Button>
            </Flex>
          )}
        </Container>
      </Box>

      {/* ===== Compliance / Security ===== */}
      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
        <Stack spacing={3} mb={{ base: 6, md: 8 }} align="center" textAlign="center">
          <Icon as={MdShield} boxSize={8} color="green.500" />
          <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
            {t('integrations.compliance.title')}
          </Heading>
          <Text color="gray.600" maxW="2xl">
            {t('integrations.compliance.subtitle', { name: platform.name })}
          </Text>
        </Stack>
        <VStack
          spacing={3}
          align="stretch"
          maxW="3xl"
          mx="auto"
          bg="white"
          borderRadius="14px"
          border="1px solid"
          borderColor="gray.100"
          p={{ base: 5, md: 6 }}
        >
          {platform.compliance.map(item => (
            <HStack key={item} align="flex-start" spacing={3}>
              <Icon as={MdCheckCircle} color="green.500" mt={0.5} flexShrink={0} />
              <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                {item}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Container>

      {/* ===== Testing Instructions for Reviewers ===== */}
      {hasTestingSteps && (
        <Box
          bg="orange.50"
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="orange.100"
          py={{ base: 12, md: 16 }}
          id="testing"
        >
          <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
            <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
              <Tag size="sm" bg="orange.500" color="white" borderRadius="full" px={3} py={1}>
                <HStack spacing={1.5}>
                  <Icon as={MdScience} boxSize={3} />
                  <Text fontSize="xs" fontWeight={700}>
                    {t('integrations.testing.badge')}
                  </Text>
                </HStack>
              </Tag>
              <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
                {t('integrations.testing.title', { name: platform.name })}
              </Heading>
              <Text color="gray.700" maxW="2xl">
                {t('integrations.testing.subtitle')}
              </Text>
            </Stack>
            <VStack
              spacing={0}
              align="stretch"
              maxW="3xl"
              mx="auto"
              bg="white"
              borderRadius="14px"
              border="1px solid"
              borderColor="orange.200"
              overflow="hidden"
              divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}
            >
              {platform.testingSteps.map(s => (
                <Flex key={s.step} p={{ base: 4, md: 5 }} gap={4} align="flex-start">
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="full"
                    bg="orange.500"
                    color="white"
                    align="center"
                    justify="center"
                    fontWeight={800}
                    fontSize="sm"
                    flexShrink={0}
                  >
                    {s.step}
                  </Flex>
                  <Box flex={1} minW={0}>
                    <Heading fontSize="md" fontWeight={700} color="gray.900" mb={1}>
                      {s.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                      {s.desc}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </VStack>
          </Container>
        </Box>
      )}

      {/* ===== FAQ ===== */}
      {hasFaq && (
        <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <Stack spacing={3} mb={{ base: 8, md: 10 }} align="center" textAlign="center">
            <Tag size="sm" colorScheme="gray" borderRadius="full" px={3}>
              <HStack spacing={1.5}>
                <Icon as={MdHelpOutline} boxSize={3} />
                <Text fontSize="xs" fontWeight={700}>
                  {t('integrations.faq.badge')}
                </Text>
              </HStack>
            </Tag>
            <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={800} color="gray.900">
              {t('integrations.faq.title')}
            </Heading>
            <Text color="gray.600" maxW="2xl">
              {t('integrations.faq.subtitle', { name: platform.name })}
            </Text>
          </Stack>
          <Box maxW="3xl" mx="auto">
            <Accordion allowToggle>
              {platform.faq.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="12px"
                  mb={3}
                  overflow="hidden"
                >
                  <AccordionButton
                    py={4}
                    px={5}
                    _hover={{ bg: 'gray.50' }}
                    _expanded={{ bg: 'gray.50' }}
                  >
                    <Text
                      flex="1"
                      textAlign="left"
                      fontWeight={600}
                      color="gray.900"
                      fontSize="sm"
                    >
                      {item.q}
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={5} px={5}>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                      {item.a}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </Container>
      )}

      {/* ===== Other integrations ===== */}
      <Box bg="gray.50" py={{ base: 12, md: 16 }} borderTop="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
          <Flex
            justify="space-between"
            align={{ base: 'flex-start', md: 'flex-end' }}
            mb={{ base: 6, md: 8 }}
            direction={{ base: 'column', md: 'row' }}
            gap={3}
          >
            <Heading fontSize={{ base: 'lg', md: 'xl' }} fontWeight={700} color="gray.900">
              {t('integrations.other.title')}
            </Heading>
            <Button
              as={RouterLink}
              to="/integrations"
              variant="ghost"
              size="sm"
              rightIcon={<MdArrowForward />}
            >
              {t('integrations.other.viewAll')}
            </Button>
          </Flex>
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={{ base: 3, md: 4 }}>
            {otherPlatforms.map(p => {
              const PIcon = p.icon;
              const isLive = (p.screenshots?.length || 0) > 0;
              return (
                <Box
                  key={p.slug}
                  as={RouterLink}
                  to={`/integrations/${p.slug}`}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="14px"
                  p={{ base: 4, md: 5 }}
                  textAlign="center"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-3px)',
                    boxShadow: 'md',
                    borderColor: `${p.color}30`,
                    textDecoration: 'none',
                  }}
                  position="relative"
                >
                  {isLive && (
                    <Tag
                      size="sm"
                      colorScheme="green"
                      position="absolute"
                      top={2}
                      right={2}
                      borderRadius="full"
                      fontSize="9px"
                      fontWeight={700}
                    >
                      {t('platforms.live')}
                    </Tag>
                  )}
                  <Flex
                    w="44px"
                    h="44px"
                    mx="auto"
                    borderRadius="12px"
                    bg={`${p.color}15`}
                    align="center"
                    justify="center"
                    color={p.color}
                    mb={2}
                  >
                    <PIcon size={20} />
                  </Flex>
                  <Text fontWeight={700} fontSize="sm" color="gray.800">
                    {p.name}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
}
